/** @jsxRuntime classic */
/** @jsx isolateComponent */

import { useState } from 'react';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors } from '@zapier/design-system';
import { css } from '@emotion/core';

import {
  EventName,
  SectionName,
  useAvroTracking,
  useTrackEvent,
} from '../../../observability';
import { Drawer } from './Drawer';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';

export type Item = {
  title: string;
  content: string;
  linkText?: string;
  href?: string;
};

type Props = {
  /**
   * Determines whether two drawers can be open at the same time or if opening one automatically closes any other open drawer.
   */
  autoClose: boolean;
  /**
   * List of Accordion drawers.
   */
  items: Array<Item>;
};

const drawerBorder = `1.577px solid ${Colors.neutral800};`;

const Styles = labelStyles('Accordion', {
  root: css({
    display: 'block',
    margin: 'auto',
    maxWidth: '768px',
  }),
  drawer: css({
    borderBottom: drawerBorder,
    '&:first-of-type': {
      borderTop: drawerBorder,
    },
  }),
});

export function Accordion({ items = [], autoClose }: Props) {
  const trackEvent = useTrackEvent();
  const [openIndex, setOpenIndex] = useState(-1);
  const { emitAccordionToggleEvent } = useAvroTracking();

  const handleToggle = ({
    index,
    isOpen,
    title,
  }: {
    index: number;
    isOpen?: boolean;
    title: string;
  }) => {
    emitAccordionToggleEvent({
      option_selected: isOpen ? 'closing' : 'opening',
      section: SectionName.FAQSection,
      title,
    });
    trackEvent(
      isOpen ? EventName.FAQ_SECTION_CLOSE : EventName.FAQ_SECTION_OPEN,
      { title },
    );
    if (autoClose) {
      setOpenIndex((prevState) => {
        const wasOpen = prevState === index;
        return wasOpen ? -1 : index;
      });
    }
  };
  return (
    <div css={Styles.root}>
      {items.map(({ title, content, href, linkText }, index) => (
        <div css={Styles.drawer} key={title}>
          <Drawer
            href={href}
            isOpen={autoClose ? openIndex === index : undefined}
            linkText={linkText}
            onToggle={({ isOpen }) => handleToggle({ index, isOpen, title })}
            title={title}
          >
            <RichTextHtml html={content} />
          </Drawer>
        </div>
      ))}
    </div>
  );
}
